import { useState, type FC } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import { parseNumber } from "@forento/shared/utilities/number"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import Button from "~/themes/school/components/Button"
import Hero from "~/themes/school/components/Hero"
import Layout from "~/themes/school/components/Layout"
import PartialLoadingPage from "~/themes/school/components/PartialLoadingPage"
import PurchaseItem from "~/themes/school/components/PurchaseItem"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

const BundlePage: FC = () => {
	const t = useTranslation()

	const bundleId = parseNumber(useParams().bundleId)

	const [paypalOrder, setPaypalOrder] = useState<{
		userStatus: "existing" | "assigned" | "created"
		email: string
	} | null>(null)

	const { data, error, refetch } = query.bundle.getPersonalized.useQuery(bundleId ?? -1)

	if (data === null) {
		return <Navigate to={routes.bundle.index()} replace />
	}

	return (
		<PurchaseSuccessModalContextProvider content={{ type: "bundle" }} paypalOrder={paypalOrder}>
			<Layout>
				{error ? (
					<p>{t("failedToLoad")}</p>
				) : !data ? (
					<PartialLoadingPage />
				) : (
					<Hero
						title={data.name}
						htmlDescription={data.longDescription?.html}
						callToAction={
							data.status === "paywall" ? (
								<PurchaseItem
									item={{
										type: "bundle",
										id: data.id,
										name: data.name,
										shortDescription: data.shortDescription,
										price: data.price,
									}}
									onPurchase={async ({ paypalOrder }) => {
										setPaypalOrder(paypalOrder)
										await refetch()
									}}
								/>
							) : data.status === "paused" ? (
								<p>{t("product.paused")}</p>
							) : data.status === "unlocked" ? (
								<Button variant="primary" isDisabled>
									{t("bundle.owned")}
								</Button>
							) : (
								exhaustiveGuard(data.status)
							)
						}
						thumbnail={
							data.thumbnailFilePath !== null ? <Thumbnail src={data.thumbnailFilePath} /> : undefined
						}
					/>
				)}
			</Layout>
		</PurchaseSuccessModalContextProvider>
	)
}

const Thumbnail = styled.img.attrs({ alt: "Bundle thumbnail" })`
	width: 100%;
	height: 100%;
	object-fit: cover;
`

export default BundlePage
